import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Coins, ArrowUp, ArrowDown, Calendar, Gift,
  PenSquare, MessageSquare, CheckCircle, Heart,
  ChevronLeft, ChevronRight, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 포인트 획득 이유별 아이콘
const reasonIcons = {
  post: PenSquare,
  comment: MessageSquare,
  attendance: CheckCircle,
  success_story: Heart,
  payment: Coins,
};

// 포인트 획득 이유별 색상
const reasonColors = {
  post: "text-blue-500 bg-blue-50",
  comment: "text-green-500 bg-green-50",
  attendance: "text-purple-500 bg-purple-50",
  success_story: "text-pink-500 bg-pink-50",
  payment: "text-gold-500 bg-gold-50",
};

export default function PointsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, token, earnPoints } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [attendanceChecked, setAttendanceChecked] = useState(false);
  const [checkingAttendance, setCheckingAttendance] = useState(false);
  const itemsPerPage = 10;

  const fetchTransactions = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await fetch(
        `${API_URL}/api/points/history?skip=${skip}&limit=${itemsPerPage}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        setTotalCount(data.total || 0);
        
        // Check if attendance was done today
        const today = new Date().toISOString().split('T')[0];
        const todayAttendance = data.transactions.find(
          t => t.reason === 'attendance' && t.created_at?.startsWith(today)
        );
        setAttendanceChecked(!!todayAttendance);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTransactions();
    }
  }, [isAuthenticated, fetchTransactions]);

  const handleAttendanceCheck = async () => {
    if (attendanceChecked || checkingAttendance) return;
    
    setCheckingAttendance(true);
    const result = await earnPoints('attendance');
    
    if (result) {
      setAttendanceChecked(true);
      fetchTransactions();
      alert(`출석 체크 완료! +${result.earned}P 적립되었습니다.`);
    } else {
      alert('출석 체크에 실패했습니다. 이미 오늘 출석하셨을 수 있습니다.');
    }
    setCheckingAttendance(false);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <Coins className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-500 mb-4">로그인이 필요합니다</p>
          <Button onClick={() => navigate('/')} className="bg-burgundy-500 hover:bg-burgundy-600">
            로그인하기
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gold-500 via-gold-600 to-amber-600 py-16">
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-yellow-300/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Coins className="w-4 h-4 text-white" />
              <span className="text-white/90 text-sm">내 포인트</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" data-testid="my-points-total">
              {(user?.points || 0).toLocaleString()}P
            </h1>
            <div className="inline-flex items-baseline gap-2 px-5 py-2 bg-white/15 backdrop-blur-sm rounded-full mb-4" data-testid="my-points-cash">
              <span className="text-white/80 text-xs">현금 환산</span>
              <span className="text-white text-xl font-bold">
                ₩{(user?.points || 0).toLocaleString()}
              </span>
              <span className="text-white/80 text-xs">원</span>
            </div>
            <p className="text-gold-100">
              포인트는 결제 시 현금처럼 사용할 수 있어요 (1P = 1원)
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Point Earn Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* 출석 체크 */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
              attendanceChecked 
                ? "bg-green-50 border-green-200" 
                : "bg-white border-stone-200 hover:border-gold-300"
            }`}
            onClick={handleAttendanceCheck}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
              attendanceChecked ? "bg-green-100" : "bg-purple-100"
            }`}>
              <CheckCircle className={`w-5 h-5 ${attendanceChecked ? "text-green-500" : "text-purple-500"}`} />
            </div>
            <h3 className="font-bold text-stone-800">출석 체크</h3>
            <p className="text-gold-600 font-semibold">+30P</p>
            {attendanceChecked && (
              <p className="text-green-500 text-xs mt-1">오늘 완료!</p>
            )}
          </motion.div>

          {/* 게시글 작성 */}
          <div className="p-4 bg-white rounded-2xl border-2 border-stone-200">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
              <PenSquare className="w-5 h-5 text-blue-500" />
            </div>
            <h3 className="font-bold text-stone-800">게시글 작성</h3>
            <p className="text-gold-600 font-semibold">+100P</p>
          </div>

          {/* 댓글 작성 */}
          <div className="p-4 bg-white rounded-2xl border-2 border-stone-200">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mb-3">
              <MessageSquare className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="font-bold text-stone-800">댓글 작성</h3>
            <p className="text-gold-600 font-semibold">+50P</p>
          </div>

          {/* 성공 스토리 */}
          <div className="p-4 bg-white rounded-2xl border-2 border-stone-200">
            <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center mb-3">
              <Heart className="w-5 h-5 text-pink-500" />
            </div>
            <h3 className="font-bold text-stone-800">성공 스토리</h3>
            <p className="text-gold-600 font-semibold">+500P</p>
          </div>
        </div>

        {/* Point Usage Info */}
        <div className="bg-gradient-to-r from-burgundy-50 to-gold-50 rounded-2xl p-6 mb-8 border border-burgundy-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-burgundy-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-burgundy-500" />
            </div>
            <div>
              <h3 className="font-bold text-burgundy-800 mb-2">포인트 사용 안내</h3>
              <ul className="text-sm text-burgundy-600 space-y-1">
                <li>• 모든 결제 시 현금처럼 사용 가능 (1P = 1원)</li>
                <li>• 멤버십 결제, 이벤트 참가비, 줌데이트 등에 적용</li>
                <li>• 포인트와 현금 결합 결제 가능</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-100">
            <h2 className="font-bold text-stone-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-stone-400" />
              포인트 내역
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-gold-300 border-t-gold-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-stone-500">불러오는 중...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-12 text-center">
              <Coins className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">포인트 내역이 없습니다</p>
              <p className="text-stone-400 text-sm mt-1">활동을 통해 포인트를 적립해보세요!</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {transactions.map((transaction, index) => {
                const Icon = reasonIcons[transaction.reason] || Coins;
                const colorClass = reasonColors[transaction.reason] || "text-stone-500 bg-stone-50";
                const isEarn = transaction.type === 'earn';
                
                return (
                  <motion.div
                    key={transaction.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 flex items-center gap-4"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass.split(' ')[1]}`}>
                      <Icon className={`w-5 h-5 ${colorClass.split(' ')[0]}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800">{transaction.description}</p>
                      <p className="text-xs text-stone-400">{formatDate(transaction.created_at)}</p>
                    </div>
                    <div className={`flex items-center gap-1 font-bold ${
                      isEarn ? "text-green-500" : "text-red-500"
                    }`}>
                      {isEarn ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                      {isEarn ? "+" : ""}{transaction.amount.toLocaleString()}P
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-stone-100 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-stone-500">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
